import { adaptiveInk } from '../theme/appearance';
import React from 'react';
import {
  useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  Paper,
  Select,
  Switch,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedFormControlLabel as FormControlLabel, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTab as Tab, LocalizedTableCell as TableCell } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import AnalysisTabBar from '../components/AnalysisTabBar';
import NorthIndianChart from '../components/charts/NorthIndianChart';
import SeoHead from '../components/seo/SeoHead';
import { useChart } from '../contexts/ChartContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { hasPremiumSubscription } from '../utils/subscription';
import {
  calculateArudhaFromDivisionalChart,
  calculateJaiminiSnapshot,
  SIGN_NAMES
} from '../utils/jaiminiCalculator';
import { useTranslation } from 'react-i18next';
import { formatDisplayDate } from '../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const dateToYmd = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

interface ChartMeta {
  key: string;
  n: number;
  name: string;
  purpose: string;
}

const SIGNIFICANCE_BY_PADA: Record<string, string> = {
  A1: 'Image / Status',
  A2: 'Wealth',
  A7: 'Marriage / Partner',
  A10: 'Career / Public Action',
  A11: 'Gains / Network'
};

const JAIMINI_TAB_META = [
  {
    title: 'Jaimini Analysis - Basic',
    description: 'Basic Jaimini view with D1 chart, Karakamsha, and Upapada Lagna for this chart.'
  },
  {
    title: 'Jaimini Analysis - All Karaks',
    description: 'View Jaimini Chara Karakas with Rahu-included or Rahu-excluded calculation rules.'
  },
  {
    title: 'Jaimini Analysis - Char Dasha',
    description: 'Review live Char Dasha with Mahadasha, Antardasha, and Pratyantardasha subdivisions.'
  },
  {
    title: 'Jaimini Analysis - Arudha Charts',
    description: 'Explore Arudha padas and Arudha-based chart layouts from live divisional data.'
  },
  {
    title: 'Jaimini Analysis - All Argalas',
    description: 'Inspect Argala patterns, obstructions, and sign-wise influences in the Jaimini framework.'
  }
];

const buildNorthChartData = (
  baseChart: any,
  lagnaSignIndex: number,
  planetsSource?: Record<string, any>,
  chartName?: string
) => ({
  ...baseChart,
  chartName,
  planets: Object.fromEntries(
    Object.entries(planetsSource || baseChart?.planets || {}).map(([key, value]) => {
      const source = value as any;
      const signNumber = Number(source?.sign?.number);
      const safeSign = Number.isFinite(signNumber) && signNumber >= 1 && signNumber <= 12 ? signNumber : 1;

      return [String(key).toUpperCase(), {
        ...source,
        name: source?.name || key,
        sign: {
          ...(source?.sign || {}),
          number: safeSign,
          name: source?.sign?.name || SIGN_NAMES[safeSign - 1],
        },
      }];
    })
  ),
  houses: {
    ...(baseChart?.houses || {}),
    ascendant: {
      ...(baseChart?.houses?.ascendant || {}),
      sign: {
        ...(baseChart?.houses?.ascendant?.sign || {}),
        number: lagnaSignIndex + 1,
        name: SIGN_NAMES[lagnaSignIndex],
      },
    },
  },
});

const JaiminiAnalysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentChart } = useChart();
  const { state: authState } = useAuth();
  const { t } = useTranslation();
  const isPremiumUser = hasPremiumSubscription(authState.user);
  const signName = React.useCallback((signIndex: number | null | undefined, fallback?: string) => {
    if (typeof signIndex === 'number' && Number.isFinite(signIndex)) {
      return t(`astrology.signs.${signIndex + 1}`, { defaultValue: fallback || SIGN_NAMES[signIndex] || '-' });
    }
    if (fallback) {
      const index = SIGN_NAMES.findIndex((name) => name.toLowerCase() === String(fallback).toLowerCase());
      if (index >= 0) return t(`astrology.signs.${index + 1}`, { defaultValue: fallback });
    }
    return fallback || '-';
  }, [t]);
  const dashaDate = React.useCallback((value: Date) => (
    formatDisplayDate(value)
  ), []);

  const [chartData, setChartData] = React.useState<any>(null);
  const [allCharts, setAllCharts] = React.useState<Record<string, any> | null>(null);
  const [availableCharts, setAvailableCharts] = React.useState<ChartMeta[]>([]);
  const [selectedDivisional, setSelectedDivisional] = React.useState<string>('D1');
  const [selectedArudha, setSelectedArudha] = React.useState<string>('A1');
  const [selectedMdKey, setSelectedMdKey] = React.useState<string>('');
  const [selectedAdKey, setSelectedAdKey] = React.useState<string>('');
  const [tab, setTab] = React.useState(0);
  const [useRahu, setUseRahu] = React.useState(true);
  const { ayanamshaMode, useKpForNadiKp } = useUserPreferences();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    document.title = 'Jaimini Analysis - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (id === 'preview') {
          if (!currentChart) {
            setError('No chart data available for preview');
            setLoading(false);
            return;
          }

          const currentChartId = currentChart?._id || currentChart?.chartId;
          if (currentChartId) {
            const chartResp = await axios.get(`${API_BASE_URL}/charts/${currentChartId}`);
            setChartData(chartResp.data.data.chart);
          } else {
            setChartData(currentChart);
          }

          if (currentChartId) {
            try {
              const divResp = await axios.get(`${API_BASE_URL}/charts/${currentChartId}/divisional`);
              setAllCharts(divResp.data.data.charts || null);
              setAvailableCharts(divResp.data.data.availableCharts || []);
            } catch (_) {
              setAllCharts(null);
              setAvailableCharts([]);
            }
          }

          setLoading(false);
          return;
        }

        const chartResp = await axios.get(`${API_BASE_URL}/charts/${id}`);
        setChartData(chartResp.data.data.chart);

        try {
          const divResp = await axios.get(`${API_BASE_URL}/charts/${id}/divisional`);
          setAllCharts(divResp.data.data.charts || null);
          setAvailableCharts(divResp.data.data.availableCharts || []);
        } catch (_) {
          setAllCharts(null);
          setAvailableCharts([]);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load Jaimini analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentChart]);

  const jaimini = React.useMemo(() => calculateJaiminiSnapshot(chartData, new Date(), useRahu), [chartData, useRahu]);

  const getCharaDashaKey = React.useCallback(
    (entry: { sign: number; start: Date } | null | undefined) => (entry ? `${entry.sign}-${entry.start.toISOString()}` : ''),
    []
  );

  React.useEffect(() => {
    if (!jaimini?.charaDasha?.length) return;
    if (!selectedMdKey) {
      const current = jaimini.currentCharaDasha || jaimini.charaDasha[0];
      setSelectedMdKey(getCharaDashaKey(current));
    }
  }, [getCharaDashaKey, jaimini, selectedMdKey]);

  const selectedMahadasha = React.useMemo(() => {
    if (!jaimini?.charaDasha?.length) return null;
    return jaimini.charaDasha.find((md) => getCharaDashaKey(md) === selectedMdKey) || jaimini.charaDasha[0];
  }, [getCharaDashaKey, jaimini, selectedMdKey]);

  React.useEffect(() => {
    if (!selectedMahadasha?.antardasha?.length) {
      setSelectedAdKey('');
      return;
    }

    const currentAntardasha = selectedMahadasha.antardasha.find((entry) => (
      jaimini?.currentCharaAntardasha
      && entry.sign === jaimini.currentCharaAntardasha.sign
      && entry.start.toISOString() === jaimini.currentCharaAntardasha.start.toISOString()
    ));

    if (!selectedAdKey || !selectedMahadasha.antardasha.some((entry) => getCharaDashaKey(entry) === selectedAdKey)) {
      setSelectedAdKey(getCharaDashaKey(currentAntardasha || selectedMahadasha.antardasha[0]));
    }
  }, [getCharaDashaKey, jaimini?.currentCharaAntardasha, selectedAdKey, selectedMahadasha]);

  const selectedAntardasha = React.useMemo(() => {
    if (!selectedMahadasha?.antardasha?.length) return null;
    return selectedMahadasha.antardasha.find((ad) => getCharaDashaKey(ad) === selectedAdKey) || selectedMahadasha.antardasha[0];
  }, [getCharaDashaKey, selectedAdKey, selectedMahadasha]);

  React.useEffect(() => {
    if (!availableCharts.length) return;
    const exists = availableCharts.some((c) => c.key === selectedDivisional);
    if (!exists) {
      setSelectedDivisional(availableCharts.some((c) => c.key === 'D1') ? 'D1' : availableCharts[0].key);
    }
  }, [availableCharts, selectedDivisional]);

  const selectedDivisionalChart = React.useMemo(() => {
    if (!allCharts) return null;
    return allCharts[selectedDivisional] || null;
  }, [allCharts, selectedDivisional]);

  const liveArudha = React.useMemo(() => {
    if (selectedDivisional === 'D1') {
      return jaimini?.arudha || null;
    }

    if (!selectedDivisionalChart?.lagna?.number || !selectedDivisionalChart?.planets) {
      return jaimini?.arudha || null;
    }

    return calculateArudhaFromDivisionalChart(selectedDivisionalChart, selectedDivisional) || jaimini?.arudha || null;
  }, [selectedDivisional, selectedDivisionalChart, jaimini]);

  const selectedArudhaSign = React.useMemo(() => {
    if (!liveArudha) return null;
    return liveArudha.padas[selectedArudha] ?? liveArudha.AL;
  }, [liveArudha, selectedArudha]);

  const northArudhaChartData = React.useMemo(() => {
    if (selectedArudhaSign === null || selectedArudhaSign === undefined) return null;

    const basePlanets = Object.keys(selectedDivisionalChart?.planets || {}).length
      ? (selectedDivisionalChart?.planets || {})
      : (chartData?.planets || {});

    return buildNorthChartData(
      chartData,
      selectedArudhaSign,
      basePlanets,
      `${selectedArudha} Arudha Lagna (North)`
    );
  }, [chartData, selectedArudha, selectedArudhaSign, selectedDivisionalChart]);

  const planetsBySelectedArudhaHouse = React.useMemo(() => {
    const mapping: Record<number, string[]> = {
      1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [], 10: [], 11: [], 12: []
    };

    if (!northArudhaChartData || selectedArudhaSign === null || selectedArudhaSign === undefined) {
      return mapping;
    }

    Object.entries(northArudhaChartData.planets || {}).forEach(([planetKey, value]) => {
      const signNumber = Number((value as any)?.sign?.number);
      if (!Number.isFinite(signNumber) || signNumber < 1 || signNumber > 12) return;

      const signIndex = signNumber - 1;
      const house = ((signIndex - selectedArudhaSign + 12) % 12) + 1;
      mapping[house].push(planetKey.toUpperCase());
    });

    return mapping;
  }, [northArudhaChartData, selectedArudhaSign]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!chartData || !jaimini) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Jaimini analysis data not available for this chart.</Alert>
      </Container>
    );
  }

  const isHoraryChart = chartData?.chartType === 'horary';

  return (
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: "var(--app-surface-neutral, #fafafa)", minHeight: '100vh' }}>
      <SeoHead
        title={`${JAIMINI_TAB_META[tab]?.title || 'Jaimini Analysis'} | Kundlistar`}
        description={JAIMINI_TAB_META[tab]?.description || 'Jaimini astrology analysis for this chart.'}
        path={id ? `/chart/${id}/jaimini` : '/chart/preview/jaimini'}
        robots="noindex,nofollow"
        keywords="jaimini analysis, char dasha, karakamsha, upapada lagna, arudha, Kundlistar"
      />
      <AnalysisTabBar chartId={id!} active="jaimini" isHoraryChart={isHoraryChart} />

      <Box sx={{ mb: 3, bgcolor: 'primary.main', p: 3, borderRadius: 2, border: '2px solid', borderColor: 'primary.dark', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
            {t('jaimini.title')}
          </Typography>
          <Typography variant="body1" color="rgba(255,255,255,0.9)">
            {chartData.birthDetails?.name} • {t('astrology.lagna')}: {signName(jaimini.lagnaSign, jaimini.lagnaName)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Box sx={{ px: 1.25, py: 0.5, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.26)' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useRahu}
                  onChange={(e) => setUseRahu(e.target.checked)}
                  sx={{
                    mr: 0.75,
                    '& .MuiSwitch-thumb': { bgcolor: useRahu ? "var(--app-surface-orange, #ffeb3b)" : "var(--app-surface-neutral, #ffffff)" },
                    '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.55)', opacity: 1 },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: "var(--app-surface-neutral, rgba(255,255,255,0.7))", opacity: 1 },
                    '& .MuiSwitch-switchBase': { color: '#ffffff' }
                  }}
                />
              }
              label={<Typography sx={{ color: 'white', fontWeight: 600 }}>{useRahu ? t('jaimini.useRahu') : t('jaimini.excludeRahu')}</Typography>}
              sx={{ m: 0, color: 'white' }}
            />
          </Box>
        </Box>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('jaimini.tabs.basic')} />
          <Tab label={t('jaimini.tabs.allKaraks')} />
          <Tab label={t('jaimini.tabs.charDasha')} />
          <Tab label={t('jaimini.tabs.arudhaCharts')} />
          <Tab label={t('jaimini.tabs.allArgalas')} />
        </Tabs>
      </Paper>

      {/* TAB 0: BASIC */}
      {tab === 0 && (
        <Grid container spacing={2}>
          {/* D1 North Indian Chart */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 1.5 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>D1 Rashi Chart</Typography>
              {(() => {
                const d1Source = allCharts?.['D1'];
                const basePlanets = d1Source?.planets || chartData?.planets || {};
                const lagnaNumber = d1Source?.lagna?.number
                  ?? (chartData?.houses?.ascendant?.sign?.number ?? (jaimini.lagnaSign + 1));
                const d1ChartData = buildNorthChartData(
                  chartData,
                  Number(lagnaNumber) - 1,
                  basePlanets,
                  'Rashi (D1)'
                );
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <NorthIndianChart chartData={d1ChartData} size={430} />
                  </Box>
                );
              })()}
            </Paper>
          </Grid>

          {/* Karakamsha + Upapada */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Grid container spacing={2} direction="column">
              {/* Karakamsha Chart */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6">Karakamsha Chart</Typography>
                    {jaimini.karakamshaName && (
                      <Chip label={`KL: ${jaimini.karakamshaName}`} color="secondary" size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    AK ({jaimini.charaKarakas.find((k) => k.karaka === 'AK')?.planet}) D9 sign used as Lagna.
                    D1 planets placed relative to Karakamsha Lagna.
                  </Typography>
                  {jaimini.karakamshaSign !== null ? (() => {
                    const basePlanets = allCharts?.['D1']?.planets || chartData?.planets || {};
                    const kmChartData = buildNorthChartData(
                      chartData,
                      jaimini.karakamshaSign,
                      basePlanets,
                      'Karakamsha'
                    );
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <NorthIndianChart chartData={kmChartData} size={360} />
                      </Box>
                    );
                  })() : (
                    <Alert severity="info">Karakamsha cannot be computed (AK longitude unavailable).</Alert>
                  )}
                </Paper>
              </Grid>

              {/* Upapada Lagna */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6">Upapada Lagna (UL)</Typography>
                    {jaimini.upapadaLagnaName && (
                      <Chip label={`UL: ${jaimini.upapadaLagnaName}`} color="warning" size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Arudha of the 12th house. Indicates spouse/marriage quality.
                  </Typography>
                  {jaimini.upapadaLagna !== null ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1.5, flex: 1, minWidth: 140 }}>
                        <Typography variant="caption" color="text.secondary">UL Sign</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{jaimini.upapadaLagnaName}</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, border: "1px solid var(--app-border, #ddd)", borderRadius: 1.5, flex: 1, minWidth: 140 }}>
                        <Typography variant="caption" color="text.secondary">12th House Sign</Typography>
                        <Typography variant="subtitle1">{SIGN_NAMES[(jaimini.lagnaSign + 11) % 12]}</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, border: "1px solid var(--app-border, #ddd)", borderRadius: 1.5, flex: 1, minWidth: 140 }}>
                        <Typography variant="caption" color="text.secondary">A7 (Darapada)</Typography>
                        <Typography variant="subtitle1">{SIGN_NAMES[jaimini.arudhaPadas.houses[7]]}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Alert severity="info">Upapada Lagna cannot be computed.</Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* TAB 1: ALL KARAKAS */}
      {tab === 1 && (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Chara Karakas {useRahu ? '(8-Karaka, Rahu included)' : '(7-Karaka, Rahu excluded)'}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Karaka</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Planet</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Degree in Sign</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jaimini.charaKarakas.map((row) => (
                  <TableRow key={row.karaka}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.karaka}</TableCell>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>{row.planet}</TableCell>
                    <TableCell>{row.degreeInSign.toFixed(3)}°</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* TAB 2: CHARA DASHA */}
      {tab === 2 && (
        <Box sx={{ p: 2, bgcolor: "var(--app-surface-neutral, #eef8f0)", border: '2px solid #2e7d52', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ color: "var(--app-ink-green, #1b5e20)", fontWeight: 'bold', mb: 0.5 }}>
            {t('jaimini.charDashaTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('jaimini.charDashaDescription')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`${t('jaimini.currentMd')}: ${signName(jaimini.currentCharaDasha?.sign, jaimini.currentCharaDasha?.signName)}`} color="success" variant="outlined" />
            <Chip label={`${t('jaimini.currentAd')}: ${signName(jaimini.currentCharaAntardasha?.sign, jaimini.currentCharaAntardasha?.signName)}`} color="success" variant="outlined" />
            <Chip label={`${t('jaimini.currentPd')}: ${signName(jaimini.currentCharaPratyantardasha?.sign, jaimini.currentCharaPratyantardasha?.signName)}`} color="success" variant="outlined" />
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "var(--app-ink-green, #1b5e20)", fontWeight: 600, mb: 1 }}>
                {t('kp.mahadasha')}
              </Typography>
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {jaimini.charaDasha.map((md) => {
                  const key = getCharaDashaKey(md);
                  const active = key === getCharaDashaKey(selectedMahadasha);
                  return (
                    <Box
                      key={key}
                      onClick={() => {
                        setSelectedMdKey(key);
                        setSelectedAdKey('');
                      }}
                      sx={{
                        p: 1,
                        mb: 0.75,
                        bgcolor: active ? "var(--app-surface-neutral, #e3f2e7)" : "var(--app-surface-neutral, white)",
                        border: `1px solid ${active ? '#2e7d52' : "var(--app-border, #b7d8c1)"}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#2e7d52' }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} color="var(--app-ink-green, #1b5e20)">
                          {signName(md.sign, md.signName)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {md.years.toFixed(2)}{t('jaimini.yearShort')}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {dashaDate(md.start)} – {dashaDate(md.end)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "var(--app-ink-green, #2e7d32)", fontWeight: 600, mb: 1 }}>
                {t('jaimini.antardashaIn', { sign: signName(selectedMahadasha?.sign, selectedMahadasha?.signName) })}
              </Typography>
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {(selectedMahadasha?.antardasha || []).map((ad) => {
                  const key = getCharaDashaKey(ad);
                  const active = key === getCharaDashaKey(selectedAntardasha);
                  return (
                    <Box
                      key={key}
                      onClick={() => setSelectedAdKey(key)}
                      sx={{
                        p: 1,
                        mb: 0.75,
                        bgcolor: active ? "var(--app-surface-neutral, #edf7ee)" : "var(--app-surface-neutral, white)",
                        border: `1px solid ${active ? '#388e3c' : "var(--app-border, #c8e6c9)"}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#388e3c' }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} color="var(--app-ink-green, #2e7d32)">
                          {signName(ad.sign, ad.signName)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ad.years.toFixed(2)}{t('jaimini.yearShort')}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {dashaDate(ad.start)} – {dashaDate(ad.end)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "var(--app-ink-green, #43a047)", fontWeight: 600, mb: 1 }}>
                {t('jaimini.pratyantardashaIn', { sign: signName(selectedAntardasha?.sign, selectedAntardasha?.signName) })}
              </Typography>
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {(selectedAntardasha?.pratyantardasha || []).map((pd) => {
                  const active = Boolean(
                    jaimini.currentCharaPratyantardasha
                    && pd.sign === jaimini.currentCharaPratyantardasha.sign
                    && pd.start.toISOString() === jaimini.currentCharaPratyantardasha.start.toISOString()
                  );

                  return (
                    <Box
                      key={getCharaDashaKey(pd)}
                      sx={{
                        p: 1,
                        mb: 0.75,
                        bgcolor: active ? "var(--app-surface-neutral, #f1faf2)" : "var(--app-surface-neutral, white)",
                        border: `1px solid ${active ? '#43a047' : "var(--app-border, #dcedc8)"}`,
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} color="var(--app-ink-green, #43a047)">
                          {signName(pd.sign, pd.signName)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pd.years.toFixed(2)}{t('jaimini.yearShort')}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {dashaDate(pd.start)} – {dashaDate(pd.end)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* TAB 3: ARUDHA CHARTS */}
      {tab === 3 && (
        <Paper sx={{ p: 1.5 }}>
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Arudha (Pada) Chart - North Style</Typography>
            <Chip label={`AL: ${liveArudha ? SIGN_NAMES[liveArudha.AL] : '-'}`} color="primary" variant="outlined" />
          </Box>

          <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Live Divisional Chart</InputLabel>
              <Select
                value={selectedDivisional}
                label="Live Divisional Chart"
                onChange={(event) => setSelectedDivisional(String(event.target.value))}
              >
                {(availableCharts.length ? availableCharts : [{ key: 'D1', n: 1, name: 'Rashi', purpose: '' }]).map((chart) => (
                  <MenuItem key={chart.key} value={chart.key}>
                    {chart.key} - {chart.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Arudha Lagna</InputLabel>
              <Select
                value={selectedArudha}
                label="Arudha Lagna"
                onChange={(event) => setSelectedArudha(String(event.target.value))}
              >
                {Array.from({ length: 12 }, (_, index) => `A${index + 1}`).map((pada) => (
                  <MenuItem key={pada} value={pada}>
                    {pada}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Chip
              label={`${selectedArudha}: ${liveArudha && selectedArudhaSign !== null ? SIGN_NAMES[selectedArudhaSign] : '-'}`}
              variant="outlined"
            />
          </Box>

          {northArudhaChartData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <NorthIndianChart chartData={northArudhaChartData} size={430} />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>Arudha North chart is not available for selected source chart.</Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  All Padas (A1-A12)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Pada</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Sign</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Meaning</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries((liveArudha || jaimini.arudha).padas).map(([pada, sign]) => (
                        <TableRow key={`pada-${pada}`}>
                          <TableCell sx={{ fontWeight: pada === selectedArudha ? 700 : 500 }}>{pada}</TableCell>
                          <TableCell>{SIGN_NAMES[sign]}</TableCell>
                          <TableCell>{SIGNIFICANCE_BY_PADA[pada] || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Planets in Houses from Selected Arudha Lagna
                </Typography>
                <Grid container spacing={0.75}>
                  {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => (
                    <Grid key={`al-house-${house}`} size={{ xs: 6, sm: 4 }}>
                      <Box sx={{ border: "1px solid var(--app-border, #d9d9d9)", borderRadius: 1.5, p: 0.75, minHeight: 78, bgcolor: "var(--app-surface-neutral, #fff)" }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>
                          H{house}
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                          {(planetsBySelectedArudhaHouse[house] || []).length
                            ? planetsBySelectedArudhaHouse[house].join(', ')
                            : '-'}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* TAB 4: ALL ARGALAS */}
      {tab === 4 && (
        isPremiumUser ? (
        <Grid container spacing={1.5}>
          {Object.entries(jaimini.argalaBySign).map(([sign, data]) => (
            <Grid key={`argala-${sign}`} size={{ xs: 12, md: 6, lg: 4 }}>
              <Paper sx={{ p: 1.25, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  {SIGN_NAMES[Number(sign)]}
                </Typography>
                {[data.second, data.fourth, data.fifth, data.eleventh].map((argala) => {
                  const statusColor = argala.status === 'strong'
                    ? '#1b7f3a'
                    : argala.status === 'obstructed'
                      ? '#c62828'
                      : argala.status === 'partial'
                        ? '#ef6c00'
                        : '#666';

                  return (
                    <Box key={`${sign}-${argala.key}`} sx={{ mb: 1.25 }}>
                      <Typography variant="body2" sx={{ color: "var(--app-ink-primary)", fontWeight: 600 }}>
                        {argala.label}: <Box component="span" sx={{ color: adaptiveInk(statusColor) }}>{argala.status}</Box>
                        {argala.type !== 'none' ? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}> ({argala.type})</Box> : null}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Argala sign: <strong>{argala.argalaSign}</strong> | Obstruction sign: <strong>{argala.obstructionSign}</strong>
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {argala.reason}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: "var(--app-ink-primary)", mt: 0.5 }}>
                        Argala planets
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                        {argala.argalaPlanets.length
                          ? argala.argalaPlanets.map((planet) => <Chip key={`${sign}-${argala.key}-argala-${planet}`} label={planet} size="small" variant="outlined" />)
                          : <Typography variant="caption" color="text.secondary">None</Typography>}
                      </Box>
                      <Typography variant="caption" display="block" sx={{ color: "var(--app-ink-red, #c62828)" }}>
                        Obstructing planets
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {argala.obstructionPlanets.length
                          ? argala.obstructionPlanets.map((planet) => <Chip key={`${sign}-${argala.key}-obstruction-${planet}`} label={planet} size="small" color="error" variant="outlined" />)
                          : <Typography variant="caption" color="text.secondary">None</Typography>}
                      </Box>
                    </Box>
                  );
                })}

                {data.specialThirdPapaArgala.active ? (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: "var(--app-surface-orange, #fff4e5)", border: "1px solid var(--app-border, #ffcc80)" }}>
                    <Typography variant="body2" sx={{ color: "var(--app-ink-orange, #ef6c00)", fontWeight: 600 }}>
                      {data.specialThirdPapaArgala.label}: {data.specialThirdPapaArgala.status}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Sign: <strong>{data.specialThirdPapaArgala.sign}</strong>
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {data.specialThirdPapaArgala.reason}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(data.specialThirdPapaArgala.planets || []).map((planet) => (
                        <Chip key={`${sign}-special-${planet}`} label={planet} size="small" color="warning" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Paper>
            </Grid>
          ))}
        </Grid>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              All Argalas is Premium
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sign-wise Argala patterns, obstruction details and special Argalas are available only for Kundlistar Studio Pro users.
            </Typography>
            <Button variant="contained" color="warning" href="/upgrade">
              Choose Plan
            </Button>
          </Paper>
        )
      )}
    </Container>
  );
};

export default JaiminiAnalysis;
